import { type z } from 'zod';

import { isDefined } from 'common';

import { ContentFreeAgenticChatPropertySchemasByAction } from './AgenticChat/schema';
import { ContentFreeFlowAiPropertySchemasByAction } from './FlowAi/schema';
import { EventCategory } from './types';

/** Chat content may contain sensitive information; invalid telemetry is discarded without logging its payload. */
export function getContentFreeChatTelemetryProperties(options: {
  category: EventCategory.AgenticChat | EventCategory.FlowAi;
  action: string;
  properties: unknown;
  propertySource: 'Application' | 'PostHog';
  onInvalidKeyPaths?: (keyPaths: string[]) => void;
}): Record<string, unknown> | undefined {
  const { category, action, properties, propertySource, onInvalidKeyPaths } = options;
  const schemas: Readonly<Record<string, z.ZodObject>> =
    category === EventCategory.AgenticChat
      ? ContentFreeAgenticChatPropertySchemasByAction
      : ContentFreeFlowAiPropertySchemasByAction;
  const schema = Object.hasOwn(schemas, action) ? schemas[action] : undefined;
  if (!isDefined(schema)) {
    return undefined;
  }
  const result =
    propertySource === 'Application'
      ? schema.safeParse(properties)
      : schema.strip().safeParse(properties);
  if (result.success) {
    return result.data;
  }
  onInvalidKeyPaths?.(
    result.error.issues.flatMap((issue) =>
      issue.code === 'unrecognized_keys'
        ? issue.keys.map((key) => [...issue.path, key].map(String).join('.'))
        : [issue.path.map(String).join('.')]
    )
  );
  return undefined;
}
